/**
 * Floating damage and heal numbers.
 *
 * The oldest feedback in the genre and the cheapest: a number over the thing you hit, rising
 * and fading. `Ev.Damage` and `Ev.Heal` (v3 §3.4) are the only inputs — there is no per-frame
 * work here at all.
 *
 * Two decisions worth keeping:
 *
 *  1. **Projected once, at the moment of the hit.** The number belongs to where the blow
 *     landed, not to wherever the body has wandered by the time it fades. Following the victim
 *     for 900 ms would cost a matrix multiply per live number per frame, and looks worse: a
 *     number that chases a running Chorister is unreadable.
 *  2. **One CSS keyframe does the whole rise and fade.** The class goes on, the compositor
 *     does the rest, and the frame loop never hears about it. Nothing here allocates after
 *     construction: the pool is built once and reused round-robin, so a Cleave into six bodies
 *     costs six `textContent` writes and six class toggles.
 */

import type { PerspectiveCamera, Vector3 } from 'three/webgpu';
import type { Vec3 } from '@stillsun/shared';

/**
 * How many numbers can be in the air at once. Twenty-four is a Cleave into a pack plus a
 * healer's whole rotation on top; past that the oldest is reclaimed mid-flight, which is
 * correct — the newest hit is the one worth reading.
 */
const POOL = 24;

/** Height above the victim's feet the number starts at, in metres. Chest, not head. */
const RISE_Y = 1.5;

/** How far the horizontal scatter goes, in CSS px, so four hits do not print on top of each other. */
const SCATTER_PX = 22;

export type FloaterKind =
  /** Damage somebody else took. Moon-white. */
  | 'hit'
  /** Damage **you** took. The one red in the raid HUD. */
  | 'mine'
  /** Healing, on anybody. Mint. */
  | 'heal';

export class FloatingNumbers {
  private readonly pool: HTMLSpanElement[] = [];
  private next = 0;
  private scatter = 0;

  constructor(root: HTMLElement) {
    for (let i = 0; i < POOL; i++) {
      const el = document.createElement('span');
      el.className = 'dmgnum';
      root.append(el);
      this.pool.push(el);
    }
  }

  /**
   * One number over a world position. `camera` is the live boom camera and `screenW`/`screenH`
   * the canvas' CSS size — the same pair every other projected marker in the client uses.
   *
   * `scratch` is the caller's `Vector3`, borrowed rather than allocated. A hit behind the eye
   * or well off the edge of the screen is dropped rather than drawn mirrored (the same view
   * space test as `render/crowd.ts` and `game/npcs.ts`, and for the same reason).
   */
  add(
    pos: Vec3,
    amount: number,
    kind: FloaterKind,
    camera: PerspectiveCamera,
    scratch: Vector3,
    screenW: number,
    screenH: number,
    crit = false,
  ): void {
    if (screenW <= 0 || screenH <= 0) return;
    scratch.set(pos.x, pos.y + RISE_Y, pos.z);
    scratch.applyMatrix4(camera.matrixWorldInverse);
    if (scratch.z >= -camera.near) return;
    scratch.applyMatrix4(camera.projectionMatrix); // divides by w on a Vector3
    if (Math.abs(scratch.x) > 1.1 || Math.abs(scratch.y) > 1.1) return;

    const el = this.pool[this.next];
    this.next = (this.next + 1) % this.pool.length;
    if (!el) return;

    // Three positions, cycled: two numbers in the same burst never print on top of each other,
    // and the pattern is short enough that a stream still reads as one column.
    this.scatter = (this.scatter + 1) % 3;
    const jitter = (this.scatter - 1) * SCATTER_PX;
    const px = Math.round((scratch.x * 0.5 + 0.5) * screenW) + jitter;
    const py = Math.round((-scratch.y * 0.5 + 0.5) * screenH);

    el.textContent = kind === 'heal' ? `+${String(amount)}` : String(amount);
    el.style.setProperty('--dx', `${String(px)}px`);
    el.style.setProperty('--dy', `${String(py)}px`);
    el.className = 'dmgnum';
    if (kind !== 'hit') el.classList.add(kind);
    if (crit) el.classList.add('crit');
    void el.offsetWidth; // restart the keyframe even mid-flight
    el.classList.add('on');
  }

  /** Leaving the raid: take every number in the air down with the HUD. */
  clear(): void {
    for (const el of this.pool) el.className = 'dmgnum';
  }
}
